import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useArealplanPages,
    useArealplanCount,
    useArealplanerByNapIds,
    useArealplanFacets,
    domainLabel,
    type Arealplan,
    type ArealplanQuery,
} from '@origo/planregister';
import { useMapViewContext } from '@origo/map-context';
import PlanLandingPage, { type ActiveFilterChip } from './PlanLandingPage';
import { useCodelistOptions } from '../../hooks/useCodelistOptions';
import { usePlanFilter } from '../../filter/usePlanFilter';
import { toArealplanQuery } from '../../filter/filterUtils';
import { emptyFilters, sortOptions, type PlanFilters } from '../../filter/types';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { createPlanSummaryMapper } from '../../mappers/toPlanSummary';
import { getRecentPlanIds } from '../../utils/recentPlans';

/** Ventetid før fritekstsøket sendes til tjenesten. */
const SEARCH_DEBOUNCE_MS = 300;

/** Telling avhenger ikke av sortering — dropp den så cachen deles på tvers av sorteringer. */
const withoutSort = ({ sort: _sort, ...rest }: ArealplanQuery): ArealplanQuery => rest;

const ALL_PLANS_QUERY = withoutSort(toArealplanQuery(emptyFilters, '', sortOptions[0]));

interface PlanLandingContainerProps {
    onSelectPlan: (napId: string) => void;
    onCreatePlan: () => void;
}

const PlanLandingContainer: React.FC<PlanLandingContainerProps> = ({ onSelectPlan, onCreatePlan }) => {
    const { t } = useTranslation();
    const { mapView } = useMapViewContext();
    const [searchValue, setSearchValue] = useState('');
    const [filterOpen, setFilterOpen] = useState(false);

    const codelists = useCodelistOptions();
    const facets = useArealplanFacets();
    const { filters, setFilters, sortOption, setSortOption, activeFilterCount } = usePlanFilter();

    // Lazy loading (#14578): 50 planer om gangen. Søk, filter og sortering
    // kjøres i tjenesten, så de gjelder hele registeret — ikke bare det som er lastet.
    const debouncedSearch = useDebouncedValue(searchValue, SEARCH_DEBOUNCE_MS);
    // Tømming av søket slår inn med en gang — debounce er bare for skriving.
    const activeSearch = searchValue.trim() === '' ? '' : debouncedSearch;
    const query = useMemo(
        () => toArealplanQuery(filters, activeSearch, sortOption),
        [filters, activeSearch, sortOption]
    );
    const pages = useArealplanPages(query);
    const matchCount = useArealplanCount(withoutSort(query));
    const totalCount = useArealplanCount(ALL_PLANS_QUERY);

    // Tomt søk + ingen aktive filtre → vis «Nylig jobbet med». Ellers treff.
    const isSearching = activeSearch.trim() !== '' || activeFilterCount > 0;

    // Lest én gang ved mount — lista endres bare når man åpner en plan (og forlater siden).
    const recentIds = useMemo(() => getRecentPlanIds(), []);
    const recentPlans = useArealplanerByNapIds(recentIds);
    // Cachet per plan, så allerede lastede kort beholder samme objekt (og ikke
    // re-rendres) når neste side kommer inn.
    const toSummary = useMemo(() => createPlanSummaryMapper(codelists.domains), [codelists.domains]);

    const recentItems = useMemo(() => {
        const byNapId = new Map((recentPlans.data ?? []).map(p => [p.nasjonalArealplanId, p]));
        return recentIds
            .map(id => byNapId.get(id))
            .filter((p): p is Arealplan => Boolean(p))
            .map(toSummary);
    }, [recentIds, recentPlans.data, toSummary]);

    const resultItems = useMemo(
        () => (pages.data?.pages ?? []).flatMap(p => p.items).map(toSummary),
        [pages.data, toSummary]
    );

    // Mens et nytt søk/filter laster, vises forrige resultat som placeholder —
    // da skal vi ikke hente «neste side» av det gamle resultatet.
    const { isFetchingNextPage, isFetchNextPageError, fetchNextPage } = pages;
    const hasMore = Boolean(pages.hasNextPage) && !pages.isPlaceholderData;
    const handleLoadMore = useCallback(() => {
        if (hasMore && !isFetchingNextPage) void fetchNextPage();
    }, [hasMore, isFetchingNextPage, fetchNextPage]);

    // Feil på første side for gjeldende søk/filter vises i resultatområdet,
    // så søk og filter fortsatt kan endres.
    const listError = useMemo(() => {
        if (!pages.isError || pages.data) return undefined;
        console.error('[planforvaltning] arealplan query error', pages.error);
        const detail = pages.error instanceof Error ? pages.error.message : String(pages.error);
        return `${t('common.errorLoading')} — ${detail}`;
    }, [pages.isError, pages.data, pages.error, t]);

    // Antall og filtervalg er ikke kritiske — lista virker uten dem, men si fra.
    const statsError = totalCount.isError || matchCount.isError || facets.isError;

    // Aktive filtre som fjernbare chips (drømmeplan-admin-mønster).
    const activeFilterChips = useMemo<ActiveFilterChip[]>(() => {
        const chips: ActiveFilterChip[] = [];
        const patch = (p: Partial<PlanFilters>) => setFilters({ ...filters, ...p });
        const d = codelists.domains;

        filters.statuses.forEach(v =>
            chips.push({ id: `status-${v}`, label: `${t('landing.filter.status')}: ${domainLabel(d, 'planstatus', v)}`, onRemove: () => patch({ statuses: filters.statuses.filter(x => x !== v) }) })
        );
        filters.plantyper.forEach(v =>
            chips.push({ id: `plantype-${v}`, label: `${t('landing.filter.plantype')}: ${domainLabel(d, 'plantype', v)}`, onRemove: () => patch({ plantyper: filters.plantyper.filter(x => x !== v) }) })
        );
        filters.lovreferanser.forEach(v =>
            chips.push({ id: `lov-${v}`, label: `${t('landing.filter.lovreferanse')}: ${domainLabel(d, 'lovreferanse', v)}`, onRemove: () => patch({ lovreferanser: filters.lovreferanser.filter(x => x !== v) }) })
        );
        filters.kommuner.forEach(v =>
            chips.push({ id: `kommune-${v}`, label: `${t('landing.filter.kommune')}: ${v}`, onRemove: () => patch({ kommuner: filters.kommuner.filter(x => x !== v) }) })
        );
        if (filters.dateFrom || filters.dateTo) {
            chips.push({ id: 'date', label: t('landing.filter.dateLabel'), onRemove: () => patch({ dateFrom: null, dateTo: null }) });
        }
        return chips;
    }, [filters, setFilters, t, codelists.domains]);

    // Zoom-til kart. Arealplan er en table uten geometri; krever separat oppslag.
    // TODO(håvard): bygg FeatureLayer-query mot napId for geometri + mapView.goTo(...).
    const handleZoomTo = useCallback((napId: string) => {
        if (!mapView) return;
        mapView.graphics.removeAll();
        console.info('[planforvaltning] zoom-to (geometri ikke koblet enda)', napId);
    }, [mapView]);

    return (
        <PlanLandingPage
            onCreatePlan={onCreatePlan}
            search={{
                value: searchValue,
                onChange: setSearchValue,
                onClear: () => setSearchValue(''),
            }}
            filter={{
                facets: facets.data,
                domains: codelists.domains,
                filters,
                onFiltersChange: setFilters,
                sortOption,
                onSortChange: setSortOption,
                showSortControl: false,
            }}
            filterOpen={filterOpen}
            onToggleFilter={() => setFilterOpen(o => !o)}
            activeFilterCount={activeFilterCount}
            activeFilterChips={activeFilterChips}
            onClearAllFilters={() => setFilters(emptyFilters)}
            sortOption={sortOption}
            onSortChange={setSortOption}
            isSearching={isSearching}
            isLoading={pages.isLoading}
            listError={listError}
            statsError={statsError}
            searchTerm={activeSearch}
            recentItems={recentItems}
            resultItems={resultItems}
            matchCount={matchCount.data}
            totalCount={totalCount.data}
            hasMore={hasMore}
            isLoadingMore={isFetchingNextPage}
            loadMoreError={isFetchNextPageError}
            onLoadMore={handleLoadMore}
            onZoomTo={handleZoomTo}
            onSelect={onSelectPlan}
        />
    );
};

export default PlanLandingContainer;
